import { AfterViewInit, DestroyRef, Directive, ElementRef, inject } from '@angular/core';

/**
 * Renders images at the size the file itself declares. An SVG referenced by URL usually carries a
 * viewBox and no width, and such an image has no intrinsic width for CSS: it stretches to whatever
 * box it is dropped into, so the same file comes out at a different size in every place it is shown.
 * The dimensions the browser resolved once the file was loaded are written onto the element, and the
 * surrounding `max-width` / `max-height` scale it down from there.
 *
 * Put it on the image, or on a container whose content is written through `innerHTML` -- images that
 * arrive with a later write are sized as well.
 */
@Directive({
  selector: '[coinIntrinsicImageSize]'
})
export class IntrinsicImageSizeDirective implements AfterViewInit {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  ngAfterViewInit(): void {
    const element = this.host.nativeElement;
    this.sizeImages();

    const observer = new MutationObserver(() => this.sizeImages());
    observer.observe(element, { childList: true, subtree: true });
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  private sizeImages(): void {
    const element = this.host.nativeElement;
    const images = element instanceof HTMLImageElement ? [element] : [...element.querySelectorAll('img')];
    images.forEach(image => this.sizeImage(image));
  }

  /** A file that is still loading has no dimensions to read yet, so it is sized once it has them. */
  private sizeImage(image: HTMLImageElement): void {
    if (!image.complete) {
      image.addEventListener('load', () => this.sizeImage(image), { once: true });
      return;
    }
    // A file that failed to load reports no dimensions; there is nothing to size then.
    if (!image.naturalWidth) return;

    image.style.width = `${image.naturalWidth}px`;
    image.style.height = 'auto';
  }
}
